import { VoiceServerEvent } from './VoiceSessionClient';

/** What a session that is up is doing, as the button shows it. */
export type VoiceActivityState = 'listening' | 'thinking' | 'speaking';

/** A microphone level at or above this is the user talking (`levelOf` is RMS × 4, so about −38 dBFS). */
export const SPEAKING_LEVEL = 0.05;

/** The server's default silence window (VoiceMode:SilenceDurationMs), after which it takes the question. */
export const QUIET_MS = 700;

interface Lookup {
    // The responses of its question that do not answer it: the earlier ones and the one that called it.
    readonly notAnswers: Set<string>;
    // The response that called it was not among them yet; the next new response of the question is that one.
    callerUnseen: boolean;
}

const addTo = (map: Map<string, Set<string>>, key: string, value: string): void => {
    const values = map.get(key) ?? new Set<string>();
    values.add(value);
    map.set(key, values);
};

/**
 * Tells thinking from listening and speaking (#2579) with the events the client already gets.
 *
 * A question is thinking from when it was heard until the first audio of its answer, or its cancel. The server says
 * when the user started talking (`speech.started`) but not when they stopped, so it counts as asked once the
 * microphone has been quiet for {@link QUIET_MS}, counted from no earlier than that event. A lookup (`tool.started`) is thinking until a response newer than
 * the one that called it shows up: its audio, or its `response.done` when it says nothing. That can be a waiting line
 * rather than the answer, which the client cannot tell apart, so the button may show listening for the rest of a long
 * lookup.
 */
export class VoiceActivity {
    private questionPending = false;
    // The questions known when the last one was heard: their late audio or cancel does not settle it.
    private earlierTurns: ReadonlySet<string> = new Set();
    // The questions heard so far, as speech.started names them. Noise is heard too, and dropped only once its
    // transcript is in, which can be after the user has started the real question.
    private readonly heardTurns = new Set<string>();
    // The responses each question has had so far, and those of them that are done.
    private readonly responses = new Map<string, Set<string>>();
    private readonly finished = new Map<string, Set<string>>();
    // The questions with a lookup under way.
    private readonly lookups = new Map<string, Lookup>();
    private lastLoudAt = Number.NEGATIVE_INFINITY;

    onEvent(event: VoiceServerEvent, now: number): void {
        const { turnId } = event;
        // A spoken refusal names no question; it follows the cancel of the newest one.
        const isNewQuestion = turnId === undefined || !this.earlierTurns.has(turnId);
        switch (event.t) {
            case 'speech.started':
                this.questionPending = true;
                // The server heard speech the microphone may hardly pick up (a quiet voice, a headset far from the
                // mouth); without this, that question would be thinking on the next tick, while the user still talks.
                this.lastLoudAt = Math.max(this.lastLoudAt, now);
                this.earlierTurns = new Set([...this.responses.keys(), ...this.lookups.keys(), ...this.heardTurns]);
                if (turnId !== undefined) {
                    this.heardTurns.add(turnId);
                }
                break;
            case 'audio':
                if (this.questionPending && isNewQuestion) {
                    this.questionPending = false;
                    // The user has moved on, and the new question is answered: an older lookup that never spoke
                    // again must not keep the button thinking.
                    this.earlierTurns.forEach((earlier) => this.lookups.delete(earlier));
                }
                this.noteResponse(turnId, event.responseId);
                break;
            case 'response.done':
                this.noteResponse(turnId, event.responseId);
                if (turnId !== undefined && event.responseId !== undefined) {
                    addTo(this.finished, turnId, event.responseId);
                }
                break;
            case 'tool.started':
                if (turnId !== undefined) {
                    const seen = this.responses.get(turnId) ?? new Set<string>();
                    const finished = this.finished.get(turnId);
                    this.lookups.set(turnId, {
                        notAnswers: new Set(seen),
                        // The server sends a call's tool.started before its response's response.done, so a response
                        // still open made it. With none open, the call was made without a word, or its audio is held
                        // back behind an earlier answer (a follow-up that chains a second lookup does both).
                        callerUnseen: [...seen].every((id) => finished?.has(id) === true),
                    });
                }
                break;
            case 'response.cancelled':
                // Not the lookups of older questions: background noise is heard and dropped (`not_heard`) while
                // a lookup goes on.
                if (isNewQuestion) {
                    this.questionPending = false;
                }
                if (turnId === undefined) {
                    this.lookups.clear();
                } else {
                    this.lookups.delete(turnId);
                }
                break;
            default:
                break;
        }
    }

    onLevel(level: number, now: number): void {
        if (level >= SPEAKING_LEVEL) {
            this.lastLoudAt = now;
        }
    }

    /** The assistant owes an answer: a heard question or a lookup under way, whatever the microphone picks up. */
    get waiting(): boolean {
        return this.questionPending || this.lookups.size > 0;
    }

    state(playing: boolean, now: number): VoiceActivityState {
        if (playing) {
            return 'speaking';
        }
        return this.waiting && now - this.lastLoudAt >= QUIET_MS ? 'thinking' : 'listening';
    }

    private noteResponse(turnId: string | undefined, responseId: string | undefined): void {
        if (turnId === undefined || responseId === undefined) {
            return;
        }
        addTo(this.responses, turnId, responseId);
        const lookup = this.lookups.get(turnId);
        // Late chunks of the response that called the lookup do not end it; anything after it does.
        if (!lookup || lookup.notAnswers.has(responseId)) {
            return;
        }
        if (lookup.callerUnseen) {
            lookup.notAnswers.add(responseId);
            lookup.callerUnseen = false;
        } else {
            this.lookups.delete(turnId);
        }
    }
}
